import { useEffect, useRef, useCallback } from 'react';
import { api } from '../api/client';

/**
 * 30-second polling hook — calls GET /api/poll?since=<last_polled>
 * and triggers a refresh callback when changes are detected.
 *
 * Usage:
 *   usePolling(() => refetchTasks(), { enabled: isAuthenticated });
 */
export function usePolling(
  onChanges: () => void,
  options: { enabled?: boolean; intervalMs?: number } = {}
) {
  const { enabled = true, intervalMs = 30_000 } = options;
  const lastPolled = useRef<number>(Math.floor(Date.now() / 1000));
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    try {
      const result = await api.poll(lastPolled.current);
      lastPolled.current = result.polledAt;
      if (result.hasChanges) onChanges();
    } catch {
      // Silently ignore poll errors (offline, etc.)
    }
  }, [onChanges]);

  useEffect(() => {
    if (!enabled) return;
    timerRef.current = setInterval(poll, intervalMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [poll, enabled, intervalMs]);
}
